import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../context/ConfigContext';

function SettingsHub() {
  const navigate = useNavigate();
  const { getTenantSubdomain, currentTenant } = useConfig();
  const subdomain = getTenantSubdomain();
  // Use custom domain if set, otherwise fall back to subdomain.deedstack.com
  const displayDomain = currentTenant?.customDomain || (subdomain ? `${subdomain}.deedstack.com` : null);

  return (
    <div className="admin-dashboard-container">
      {/* Header with Back Button */}
      <div className="admin-hub-header">
        <button
          onClick={() => navigate('/admin')}
          className="admin-hub-back-btn"
        >
          <span className="admin-hub-back-arrow">←</span>
          Back to Dashboard
        </button>
        <h1 className="admin-page-title">Settings</h1>
        <p className="admin-hub-subtitle">Configure your platform branding, features, and content</p>
      </div>

      {/* Your Website URL */}
      {displayDomain && (
        <div className="card" style={{
          marginBottom: '25px',
          padding: '20px',
          background: 'linear-gradient(135deg, var(--color-surface) 0%, var(--color-info-bg) 100%)',
          border: '1px solid var(--color-info-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ fontSize: '1.5rem' }}>🌐</span>
                <h3 style={{ margin: 0, color: 'var(--color-brand-text)' }}>Your Website</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <a
                  href={`https://${displayDomain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--color-brand-text)',
                    fontWeight: '600',
                    fontSize: '1.1rem',
                    textDecoration: 'none'
                  }}
                >
                  {displayDomain}
                </a>
                <span style={{
                  background: '#22c55e',
                  color: 'white',
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  ACTIVE
                </span>
              </div>
              <p style={{ margin: '8px 0 0', color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
                This is where your customers access your land financing portal
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.open(`https://${displayDomain}`, '_blank')}
                className="btn btn-primary"
                style={{ fontSize: '0.9rem' }}
              >
                Visit Site
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`https://${displayDomain}`);
                  alert('URL copied to clipboard!');
                }}
                className="btn btn-secondary"
                style={{ fontSize: '0.9rem' }}
              >
                Copy URL
              </button>
              <button
                onClick={() => navigate('/admin/settings/custom-domain')}
                className="btn btn-secondary"
                style={{ fontSize: '0.9rem' }}
              >
                Custom Domain
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hub Cards Grid */}
      <div className="admin-hub-grid">
        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/branding')}>
          <div className="admin-hub-card-icon">🎨</div>
          <h2>Branding</h2>
          <p>Customize your platform's colors, logo, and visual identity. Update theme settings to match your brand.</p>
          <button className="btn btn-primary">Customize Theme</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/business')}>
          <div className="admin-hub-card-icon">⚙️</div>
          <h2>Business Settings</h2>
          <p>Configure processing fees, interest rates, loan terms, and other business parameters.</p>
          <button className="btn btn-primary">Configure</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/square')}>
          <div className="admin-hub-card-icon">💳</div>
          <h2>Payment Settings</h2>
          <p>Configure Square credentials to accept customer payments directly to your account.</p>
          <button className="btn btn-primary">Configure Payments</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/features')}>
          <div className="admin-hub-card-icon">🔧</div>
          <h2>Features</h2>
          <p>Enable or disable platform features. Control what functionality is available to your customers.</p>
          <button className="btn btn-primary">Manage Features</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/faq')}>
          <div className="admin-hub-card-icon">❓</div>
          <h2>FAQ</h2>
          <p>Manage frequently asked questions. Add, edit, or remove Q&A items shown to customers.</p>
          <button className="btn btn-primary">Edit FAQs</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/page-content')}>
          <div className="admin-hub-card-icon">📝</div>
          <h2>Page Content</h2>
          <p>Edit content on your Home and About pages. Customize messaging and descriptions.</p>
          <button className="btn btn-primary">Edit Content</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/backup-export')}>
          <div className="admin-hub-card-icon">💾</div>
          <h2>Backup & Export</h2>
          <p>Download your data for backup or migration. Export properties, customers, loans, and payments.</p>
          <button className="btn btn-primary">Export Data</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/custom-domain')}>
          <div className="admin-hub-card-icon">🌐</div>
          <h2>Custom Domain</h2>
          <p>Connect your own domain to your DeedStack platform. Use your brand's URL for customer access.</p>
          <button className="btn btn-primary">Configure Domain</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/email-templates')}>
          <div className="admin-hub-card-icon">📧</div>
          <h2>Email Templates</h2>
          <p>Customize automated email messages sent to customers. Edit welcome, payment, and notification emails.</p>
          <button className="btn btn-primary">Edit Templates</button>
        </div>

        <div className="card admin-menu-card" onClick={() => navigate('/admin/settings/users')}>
          <div className="admin-hub-card-icon">👥</div>
          <h2>Team Members</h2>
          <p>Manage admin users and permissions. Invite team members to help manage your platform.</p>
          <button className="btn btn-primary">Manage Team</button>
          <span className="settings-pro-badge">PRO</span>
        </div>
      </div>
    </div>
  );
}

export default SettingsHub;
